// src/pages/container_rental/components/RentalSearchBar.tsx
//
// 검색 · 필터 바.
//
// 구 "오퍼레이터 콘솔"(터미널 프롬프트 >_ · 스텐실 토글 · mono 대문자 라벨 ·
// 각진 슬랩 + 폴더 탭)을 걷어내고 확정 디자인 시스템으로 맞췄다.
//  · 흰 서피스 + #E4E7EA 보더 + radius 12
//  · 인풋/셀렉트 radius 8, 필드 배경 #F9FAFB, 포커스는 액센트
//  · 상태 토글 칩은 흰 바탕, 선택 시 액센트 틴트(#FDEDE8) + #C93A14

import { useEffect, useRef, useState } from "react";
import {
  Box,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { designTokens } from "../../../theme.ts";
import {
  RentalListParams,
  RentalStatus,
} from "../../../contracts/container_rental/types.ts";

const T = designTokens;

const C = {
  surface: "#FFFFFF",
  field: "#F9FAFB",
  border: "#E4E7EA",
  borderStrong: "#D7DBDF",
  ink: T.ink,
  sub: "#6B7178",
  accent: T.cargo,
  accentHover: "#C93A14",
  accentTint: "#FDEDE8",
  neutral: "#F2F4F5",
};

interface RentalSearchBarProps {
  value: RentalListParams;
  onChange: (next: RentalListParams) => void;
  /** 정렬 셀렉트를 숨긴다. 정렬이 의미 없는 화면(타임라인)에서 사용. */
  hideSort?: boolean;
}

const STATUS_OPTIONS: Array<{ value: RentalStatus | ""; label: string }> = [
  { value: "",          label: "전체"      },
  { value: "active",    label: "진행중"    },
  { value: "paused",    label: "일시정지"  },
  { value: "closed",    label: "종료"      },
  { value: "cancelled", label: "취소"      },
  { value: "draft",     label: "초안"      },
];

const SORT_OPTIONS: Array<{
  value: NonNullable<RentalListParams["sort"]>;
  label: string;
}> = [
  { value: "updated_at_desc", label: "최근 수정 순"   },
  { value: "updated_at_asc",  label: "오래된 수정 순" },
  { value: "created_at_desc", label: "최근 등록 순"   },
  { value: "created_at_asc",  label: "오래된 등록 순" },
];

export default function RentalSearchBar({
  value,
  onChange,
  hideSort = false,
}: RentalSearchBarProps) {
  const [text, setText] = useState(value.q ?? "");
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if ((value.q ?? "") !== text) {
      setText(value.q ?? "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.q]);

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    setText(next);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onChange({ ...value, q: next, page: 0 });
    }, 300);
  };

  const handleStatusChange = (
    _e: React.MouseEvent<HTMLElement>,
    nextStatus: RentalStatus | "" | null
  ) => {
    if (nextStatus === null) return;
    onChange({ ...value, status: nextStatus, page: 0 });
  };

  const handleSortChange = (
    e: React.ChangeEvent<{ value: unknown }> | { target: { value: unknown } }
  ) => {
    onChange({
      ...value,
      sort: e.target.value as NonNullable<RentalListParams["sort"]>,
      page: 0,
    });
  };

  return (
    <Box
      sx={{
        bgcolor: C.surface,
        border: `1px solid ${C.border}`,
        borderRadius: "12px",
        boxShadow: "0 1px 2px rgba(16,24,32,0.06)",
        p: { xs: 1.75, md: 2 },
        mb: 2.5,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", lg: "row" },
          gap: { xs: 1.5, lg: 2 },
          alignItems: { lg: "center" },
          flexWrap: "wrap",
        }}
      >
        {/* 검색어 */}
        <TextField
          value={text}
          onChange={handleTextChange}
          placeholder="거래처 / 현장 / 담당자 / 연락처 / 주소"
          size="small"
          fullWidth
          sx={{
            minWidth: { xs: "100%", lg: 300 },
            flex: { lg: 1 },
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
              bgcolor: C.field,
              fontFamily: T.fonts.sans,
              fontSize: 14,
            },
            "& .MuiInputBase-input": { py: "9px" },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start" sx={{ mr: 0.75 }}>
                <SearchIcon sx={{ fontSize: 18, color: C.sub }} />
              </InputAdornment>
            ),
          }}
        />

        {/* 상태 필터 칩 */}
        <ToggleButtonGroup
          value={value.status ?? ""}
          exclusive
          onChange={handleStatusChange}
          size="small"
          sx={{
            flexWrap: "wrap",
            gap: 0.6,
            "& .MuiToggleButton-root": {
              border: `1px solid ${C.border}`,
              borderRadius: "8px",
              bgcolor: C.surface,
              fontFamily: T.fonts.sans,
              fontWeight: 500,
              fontSize: 13,
              px: 1.5,
              py: 0.5,
              color: C.ink,
              textTransform: "none",
              letterSpacing: 0,
              "&:hover": { bgcolor: C.neutral },
              "&.Mui-selected": {
                bgcolor: C.accentTint,
                borderColor: C.accentTint,
                color: C.accentHover,
                fontWeight: 600,
                "&:hover": { bgcolor: C.accentTint },
              },
            },
            "& .MuiToggleButtonGroup-grouped:not(:first-of-type)": {
              marginLeft: 0,
              borderLeft: `1px solid ${C.border}`,
              "&.Mui-selected": { borderLeftColor: C.accentTint },
            },
          }}
        >
          {STATUS_OPTIONS.map((opt) => (
            <ToggleButton key={opt.value || "all"} value={opt.value}>
              {opt.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        {/* 정렬 */}
        {!hideSort && (
          <FormControl
            size="small"
            sx={{
              minWidth: 180,
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                bgcolor: C.field,
                fontFamily: T.fonts.sans,
                fontSize: 13.5,
              },
              "& .MuiInputLabel-root": {
                fontFamily: T.fonts.sans,
                fontSize: 13,
                fontWeight: 500,
                letterSpacing: 0,
                color: C.sub,
              },
              "& .MuiInputLabel-root.Mui-focused": { color: C.accent },
            }}
          >
            <InputLabel id="rental-sort-label" shrink>정렬</InputLabel>
            <Select
              labelId="rental-sort-label"
              label="정렬"
              notched
              value={value.sort ?? "updated_at_desc"}
              onChange={handleSortChange as any}
            >
              {SORT_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>
    </Box>
  );
}
